import React from 'react';

import { BASE, HOME } from '../../captivision.config';
import { CVHeading } from '../../document.component';
import { UI } from '../../rail-document.component';
import { CVNext, CVFigs, Shot } from '../../shell.component';

// Get started, in English. Every other get-started.<language>.jsx here is the
// same page translated: keep their structure - headings, ids, screenshots - in
// step with this one. <UI> names a control the way the app labels it in that
// language.

const NEEDS = [
    { name: 'One iPad or Mac', body: <>An iPad with iPadOS 17 or later, or a Mac with Apple silicon and macOS 14 or later, with <strong>CaptiVision Host</strong>. This is where the session runs. Keep it plugged in for sessions longer than half an hour.</> },
    { name: 'One or more iPhones', body: <>Running iOS 15.6 or later, with <strong>CaptiVision Client</strong>. Each phone is one camera. Up to six in a room.</> },
    { name: 'One Wi-Fi network', body: 'The iPad or Mac and every phone must be on the same Wi-Fi. No internet connection is needed during the session.' },
    { name: 'Something to hold the phones', body: 'A small tripod or stand for each phone, so it can be set down and left alone.' }
]

const page = {
    title: 'Get started',
    eyebrow: 'User guide',
    lede: 'Your first session, from a box of phones to live captions with names. It takes about ten minutes the first time and less after that.',
    body: <>
        <CVHeading id="need" num="00"> What you need </CVHeading>
        <div className="cv-rowswrap">
            <table className="cv-rows">
                <tbody>
                    {NEEDS.map(need => (
                        <tr key={need.name}>
                            <th> {need.name} </th>
                            <td> {need.body} </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        <ol className="cv-walk">
            <li id="install">
                <div>
                    <h3> Install the apps </h3>
                    <p>Install <strong>CaptiVision Host</strong> on the iPad or Mac and <strong>CaptiVision Client</strong> on each iPhone from the App Store. The Client app is free. The Host app is free for its first week.</p>
                </div>
            </li>

            <li id="start">
                <div>
                    <h3> Start a session on the iPad or Mac </h3>
                    <p>Open CaptiVision Host. Type a <UI>Session name</UI> that people will recognise, such as "Room 204 Science", and tap <UI>Start session</UI>.</p>
                    <p>The first time, a short tour points out each button along the top. You can see it again at any time with the <UI>?</UI> button.</p>
                    <p className="cv-tip"><b>On a Mac</b>CaptiVision Host is the same app. Click wherever this guide says tap.</p>
                </div>
            </li>

            <li id="captions">
                <div>
                    <h3> Get captions ready </h3>
                    <p>Captions are made on the iPad or Mac, so it needs a speech engine before it can write them. If the top of the screen says captions are off, tap <UI>Set up</UI>.</p>
                    <ul>
                        <li>On iPadOS 26 or macOS 26 or later, Apple's built-in speech is ready to use. The iPad or Mac may download your language once.</li>
                        <li>On earlier versions, download a Whisper model. <strong>Small</strong> (190 MB) is a good start.</li>
                    </ul>
                    <p>Tap <UI>Check captioning works</UI>. The iPad or Mac plays an 11-second sample and shows you the captions it wrote. Do this before people arrive.</p>
                    <Shot name="hostSpeech" />
                </div>
            </li>

            <li id="add">
                <div>
                    <h3> Add the phones </h3>
                    <p>On the iPad or Mac, tap <UI>Add camera</UI> to show the pairing code. Then, on each iPhone:</p>
                    <ul>
                        <li>Open CaptiVision Client and tap <UI>Scan the host's code</UI>. Point the phone at the code on the iPad or Mac.</li>
                        <li>No clear view of the iPad or Mac? Pick the session from the list under <UI>On this network</UI>, or use <UI>Paste a pairing link</UI>.</li>
                        <li>The phone shows what it will do: stream video and sound to the iPad or Mac, and record nothing on the phone. Check the camera's name, leave the role as <strong>Camera</strong>, and tap <UI>Start streaming</UI>.</li>
                    </ul>
                    <CVFigs layout="pair">
                        <Shot name="hostAddCamera" caption="On the iPad or Mac: Add camera" />
                        <Shot name="cameraScan" caption="On the iPhone: scan the code" />
                    </CVFigs>
                    <p>The phone's picture appears on the iPad or Mac within a few seconds.</p>
                    <p className="cv-tip"><b>Tip</b>Codes last ten minutes. If a phone says the code has expired, tap <UI>New code</UI> on the iPad or Mac.</p>
                </div>
            </li>

            <li id="dnd">
                <div>
                    <h3> Turn on Do Not Disturb </h3>
                    <p>A phone call takes over an iPhone's camera, which would cut that angle for everyone. Each phone checks for Do Not Disturb when it starts streaming. If it is off, the app shows exactly where the switch is and closes by itself once you turn it on.</p>
                    <CVFigs layout="phones">
                        <Shot name="cameraDnd" />
                        <Shot name="cameraDndSteps" />
                    </CVFigs>
                </div>
            </li>

            <li id="place">
                <div>
                    <h3> Place the phones </h3>
                    <ul>
                        <li>Point each phone at the people who will talk, so their faces are clearly visible. A bit below eye level works well.</li>
                        <li>Keep phones within a few metres of the people they film. The closer the phone, the better the sound for captions.</li>
                        <li>Use the level line on the phone's screen. It disappears once the phone is straight.</li>
                        <li>If you are using an interpreter, give them their own phone and choose <strong>Interpreter</strong> as its role.</li>
                    </ul>
                    <p>After a few minutes the phone may darken its screen to stay cool. It is still filming. Tap the screen to wake it.</p>
                </div>
            </li>

            <li id="live">
                <div>
                    <h3> You are live </h3>
                    <p>When someone talks, an amber outline appears around them and captions run along the bottom of the screen with their name in front. Choose what the room sees with the view buttons at the top:</p>
                    <ul>
                        <li><strong>Grid</strong> shows every camera at once.</li>
                        <li><strong>Auto</strong> fills the screen with whoever is speaking.</li>
                        <li><strong>Two-Feed</strong> keeps one camera, often the interpreter, beside the speaker.</li>
                    </ul>
                    <Shot name="hostGrid" caption="Grid view: every camera, with the speaker framed in amber and named captions below." />
                    <p>To name someone, tap the badge next to their caption, or open the Speakers list. Everything they already said updates with the new name.</p>
                </div>
            </li>

            <li id="finish">
                <div>
                    <h3> Finish the session </h3>
                    <p>Tap <UI>Leave</UI> on the iPad or Mac and confirm. Every phone disconnects and goes back to normal. The phones keep nothing from the session.</p>
                    <p>Want a copy? Tap <UI>Record</UI> during the session and choose a folder. You get each camera's video, the captions and a written transcript.</p>
                </div>
            </li>
        </ol>

        <CVNext
            back={{ to: HOME, kicker: 'Back to', label: 'CaptiVision home' }}
            next={{ to: `${BASE}/guide`, kicker: 'Next', label: 'Full guide' }} />
    </>
}

export default page;
